/**
 * Cashflow formatting and calculation utilities.
 */

import type { LiquidityStatus, CovenantStatus, ActionPriority, WeeklyForecastRow, ForecastKPIs } from './types';

export function formatCurrency(value: number, compact = false): string {
  if (compact) {
    const abs = Math.abs(value);
    if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `${(value / 1_000).toFixed(0)}K`;
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export function formatPct(value: number, decimals = 1): string {
  return `${value.toFixed(decimals)}%`;
}

export function formatWeekLabel(weekStart: string): string {
  const d = new Date(weekStart + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function formatRetrievedAt(value?: string | null): string {
  if (!value) return 'As of date unavailable';

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return 'As of date unavailable';

  return `As of ${d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })}`;
}

export function liquidityColor(status: LiquidityStatus): string {
  switch (status) {
    case 'GREEN': return 'text-emerald-600 dark:text-emerald-400';
    case 'YELLOW': return 'text-amber-600 dark:text-amber-400';
    case 'RED': return 'text-red-600 dark:text-red-400';
  }
}

export function liquidityBg(status: LiquidityStatus): string {
  switch (status) {
    case 'GREEN': return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300';
    case 'YELLOW': return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
    case 'RED': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
  }
}

export function covenantBg(status: CovenantStatus): string {
  switch (status) {
    case 'COMPLIANT': return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300';
    case 'CAUTION': return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
    case 'BREACH': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
  }
}

export function priorityBg(priority: ActionPriority): string {
  switch (priority) {
    case 'critical': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
    case 'high': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300';
    case 'medium': return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
    case 'low': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
  }
}

export function sortData<T extends Record<string, any>>(data: T[], field: string, dir: string): T[] {
  if (!field) return data;
  return [...data].sort((a, b) => {
    const aVal = a[field];
    const bVal = b[field];
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return dir === 'ASC' ? aVal - bVal : bVal - aVal;
    }
    const cmp = String(aVal).localeCompare(String(bVal));
    return dir === 'ASC' ? cmp : -cmp;
  });
}

export function computeKPIs(weeks: WeeklyForecastRow[]): ForecastKPIs {
  if (!weeks.length) {
    return {
      total_receipts_13w: 0,
      total_disbursements_13w: 0,
      net_cash_flow_13w: 0,
      minimum_cash_week: 0,
      minimum_cash_amount: 0,
      ending_cash_week13: 0,
      average_weekly_burn: 0,
      weeks_of_runway: 0,
      covenant_breach_week: null,
      utilization_peak_pct: 0,
    };
  }

  const totalReceipts = weeks.reduce((s, w) => s + w.total_receipts, 0);
  const totalDisbursements = weeks.reduce((s, w) => s + w.total_disbursements, 0);
  const netCashFlow = totalReceipts - totalDisbursements;

  let minCash = Infinity;
  let minWeek = 1;
  let covenantBreachWeek: number | null = null;
  let peakUtilization = 0;

  for (const w of weeks) {
    if (w.ending_cash < minCash) {
      minCash = w.ending_cash;
      minWeek = w.week_number;
    }
    if (w.covenant_status === 'BREACH' && covenantBreachWeek === null) {
      covenantBreachWeek = w.week_number;
    }
    if (w.revolver_outstanding > 0) {
      const util = (w.revolver_outstanding / (w.revolver_outstanding + w.available_credit)) * 100;
      peakUtilization = Math.max(peakUtilization, util);
    }
  }

  const avgBurn = totalDisbursements / weeks.length;
  const lastWeek = weeks[weeks.length - 1];
  const weeksOfRunway = avgBurn > 0 ? lastWeek.ending_cash / avgBurn : 999;

  return {
    total_receipts_13w: totalReceipts,
    total_disbursements_13w: totalDisbursements,
    net_cash_flow_13w: netCashFlow,
    minimum_cash_week: minWeek,
    minimum_cash_amount: minCash === Infinity ? 0 : minCash,
    ending_cash_week13: lastWeek.ending_cash,
    average_weekly_burn: avgBurn,
    weeks_of_runway: weeksOfRunway,
    covenant_breach_week: covenantBreachWeek,
    utilization_peak_pct: peakUtilization,
  };
}
